export type PrismConfigValueType = "bool" | "int";

// API 응답 타입 (백엔드 PrismConfigItem과 일치)
export interface PrismConfigApiItem {
  name: string;
  value: boolean | number | string | object;
}

// UI에서 사용하는 타입
export interface PrismConfigBoolItem {
  name: string; // e.g. "COLLECT_BLOCK"
  type: "bool";
  label: string;
  tooltip?: string;
  value: boolean;
}

export interface PrismConfigIntItem {
  name: string; // e.g. "MAX_CONCURRENT_JOBS"
  type: "int";
  label: string;
  tooltip?: string;
  value: number;
  min?: number; // default 1
}

export type PrismConfigItem = PrismConfigBoolItem | PrismConfigIntItem;

export interface PrismConfigGroup {
  title: string;
  items: PrismConfigItem[];
}

export interface PrismConfigState {
  groups: PrismConfigGroup[];
}

export type UniversalThrottlingChannel = string;

export type UniversalThrottlingMethodName = "sleep_sec" | "max_concurrency" | "max_run_time_sec";

export type UniversalThrottlingMethod =
  | { method: "sleep_sec"; value: number }
  | { method: "max_concurrency"; value: number }
  | { method: "max_run_time_sec"; value: number };

export interface UniversalThrottlingPolicy {
  id: string;
  channel: UniversalThrottlingChannel;
  throttlingKey: string;
  throttlingMethod: UniversalThrottlingMethod[];
}

export interface CreateUniversalThrottlingPolicyDto {
  channel: UniversalThrottlingChannel;
  throttlingKey: string;
  throttlingMethod: UniversalThrottlingMethod[];
}

export type UpdateUniversalThrottlingPolicyDto = CreateUniversalThrottlingPolicyDto;

export const ANOMALY_CHECK_TYPES = [
  "wrong_unique_field_value",
  "number_greater_than_zero",
  "row_count_limit_exceeded",
  "row_count_percentage_difference",
  "collection_time_percentage_difference",
  "file_size_percentage_difference",
  "missing_data",
] as const;

export type AnomalyDetectionCheckType = (typeof ANOMALY_CHECK_TYPES)[number];

// ChannelType.list()가 프로젝트 밖이라 일단 string으로 둠 (API 연결 시 서버 값으로 대체)
export type AnomalyDetectionChannel = string;

export type AnomalyDetectionKwargs =
  | { unique_field_name: string }
  | { field: string }
  | { row_count_limit: number }
  | { percentage_threshold: number }
  | { key_rules: Record<string, unknown>[]; unique_fields: string[] };

export interface AnomalyDetectionRule {
  id: number;
  channel: AnomalyDetectionChannel;
  reportType: string; // "*" or "-" or report_type name
  checkType: AnomalyDetectionCheckType;
  kwargsByCheckType: Record<string, unknown>;
}

export interface CreateAnomalyDetectionRuleDto {
  channel: AnomalyDetectionChannel;
  reportType: string;
  checkType: AnomalyDetectionCheckType;
  kwargsByCheckType: Record<string, unknown>;
}

export type UpdateAnomalyDetectionRuleDto = CreateAnomalyDetectionRuleDto;

export type DataForwarderChannel = string; // ["all", ...ChannelType.list()] (일단 string)

export type DataForwarderRegisterPhase = 1 | 2 | 3;

export interface S3Destination {
  bucket_name: string;
  bucket_region: string;
  role_arn: string;
  prefix?: string;
  external_id: string;
}

export interface GCSDestination {
  bucket_name: string;
  target_service_account: string;
  prefix?: string;
}

export interface DataForwarderPolicy {
  id: number;
  register_phase?: DataForwarderRegisterPhase;
  org_id: string;
  workspace_id?: string | null;
  policy_name: string;
  forwarding_type: "s3" | "gcs";
  channel?: DataForwarderChannel | null;
  sub_channel?: string;
  dw_account_id?: string;
  reports?: string | null;
  destination_info?: Record<string, unknown> | string | null;
}

export interface CreateDataForwarderPolicyStep1Dto {
  policy_name: string;
  org_id: string;
  workspace_id?: string;
  channel?: DataForwarderChannel;
  sub_channel?: string;
  dw_account_id?: string;
  reports?: string;
  forwarding_type: "s3" | "gcs";
  external_id?: string; // S3에만 필요
}

export interface UpdateDataForwarderPolicyStep2S3Dto {
  forwarding_type: "s3";
  bucket_region: string;
  bucket_name: string;
  role_arn: string;
  prefix?: string;
  external_id: string;
}

export interface UpdateDataForwarderPolicyStep2GCSDto {
  forwarding_type: "gcs";
  bucket_name: string;
  target_service_account: string;
  prefix?: string;
}

export type UpdateDataForwarderPolicyStep2Dto =
  | UpdateDataForwarderPolicyStep2S3Dto
  | UpdateDataForwarderPolicyStep2GCSDto;

export type DataForwarderConnectionTestStatus = "" | "testing" | "success" | "failed";

export interface FacebookResourceTargetDateRow {
  adAccountId: string;
  targetDate: string; // YYYY-MM-DD
}

export interface FacebookResourceTargetDateState {
  defaultBackwardDays: number;
  rows: FacebookResourceTargetDateRow[];
}

export interface UpsertFacebookResourceTargetDateDto {
  adAccountId: string;
  targetDate: string; // YYYY-MM-DD
}

export interface UpdateFacebookResourceDefaultBackwardDaysDto {
  defaultBackwardDays: number;
}

export interface ChannelDelayThresholdRow {
  channel: string;
  subChannel?: string; // 없으면 undefined
  hours: string; // input용 (문자열)
  minutes: string; // input용 (문자열)
}

export interface ChannelDelayThresholdsState {
  rows: ChannelDelayThresholdRow[];
  isLoading: boolean;
}

export type ChannelDelayThresholdsMap = Record<string, number>; // key: "channel" or "channel:subChannel" (seconds)
